import {
	PDFDocument,
	PDFName,
	PDFArray,
	PDFString,
	PDFHexString,
	StandardFonts,
	rgb,
} from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import pako from 'pako';
import { downloadFromS3Signed } from 'src/_shared/services/utils';

const S3_BUCKET = 'erin-documents';

export async function fetchS3Bytes(s3Key) {
	const signedUrl = await downloadFromS3Signed(s3Key, S3_BUCKET);
	if (!signedUrl) throw new Error('Failed to get signed URL');
	const response = await fetch(signedUrl);
	return new Uint8Array(await response.arrayBuffer());
}

/**
 * Convert a Uint8Array to a Latin-1 string (byte-for-byte mapping).
 */
function bytesToLatin1(bytes) {
	let result = '';
	for (let i = 0; i < bytes.length; i++) {
		result += String.fromCharCode(bytes[i]);
	}

	return result;
}

/**
 * Tokenize a decoded PDF content stream into an array of tokens.
 * Each token has a type ('number', 'string', 'name', 'operator', 'array', 'hexstring')
 * and a value.
 */
function tokenizeContentStream(text) {
	const tokens = [];
	let i = 0;

	while (i < text.length) {
		// Skip whitespace
		if (/\s/.test(text[i])) {
			i++;
			continue;
		}

		// Skip comments
		if (text[i] === '%') {
			while (i < text.length && text[i] !== '\n') i++;
			continue;
		}

		// String literal: (text with \escapes and (nested) parens)
		if (text[i] === '(') {
			let depth = 1;
			let str = '';
			i++;
			while (i < text.length && depth > 0) {
				if (text[i] === '\\') {
					str += text[i] + (text[i + 1] || '');
					i += 2;
				} else if (text[i] === '(') {
					depth++;
					str += text[i];
					i++;
				} else if (text[i] === ')') {
					depth--;
					if (depth > 0) str += text[i];
					i++;
				} else {
					str += text[i];
					i++;
				}
			}

			tokens.push({ type: 'string', value: str });
			continue;
		}

		// Hex string: <48656C6C6F>
		if (text[i] === '<' && (i + 1 >= text.length || text[i + 1] !== '<')) {
			let hex = '';
			i++;
			while (i < text.length && text[i] !== '>') {
				hex += text[i];
				i++;
			}

			if (i < text.length) i++;
			tokens.push({ type: 'hexstring', value: hex });
			continue;
		}

		// Name: /SomeName
		if (text[i] === '/') {
			let name = '';
			i++;
			while (i < text.length && /[^\s/<>[\](){}%]/.test(text[i])) {
				name += text[i];
				i++;
			}

			tokens.push({ type: 'name', value: name });
			continue;
		}

		// Array: [...] (used by TJ operator)
		if (text[i] === '[') {
			let depth = 1;
			let content = '';
			i++;
			while (i < text.length && depth > 0) {
				if (text[i] === '[') depth++;
				else if (text[i] === ']') {
					depth--;
					if (depth === 0) {
						i++;
						break;
					}
				}

				content += text[i];
				i++;
			}

			tokens.push({ type: 'array', value: content });
			continue;
		}

		// Number: 123, -1.23, +0.5, .5
		if (
			/[0-9.]/.test(text[i]) ||
			((text[i] === '-' || text[i] === '+') && /[0-9.]/.test(text[i + 1] || ''))
		) {
			let num = '';
			if (text[i] === '-' || text[i] === '+') {
				num += text[i];
				i++;
			}

			while (i < text.length && /[0-9.]/.test(text[i])) {
				num += text[i];
				i++;
			}

			tokens.push({ type: 'number', value: num });
			continue;
		}

		// Dict markers
		if (text[i] === '<' && text[i + 1] === '<') {
			i += 2;
			continue;
		}

		if (text[i] === '>' && text[i + 1] === '>') {
			i += 2;
			continue;
		}

		// Operator keyword: BT, ET, Tm, Td, Tf, Tj, TJ, rg, g, etc.
		if (/[a-zA-Z*'"]/.test(text[i])) {
			let op = '';
			while (i < text.length && /[a-zA-Z*'"]/.test(text[i])) {
				op += text[i];
				i++;
			}

			tokens.push({ type: 'operator', value: op });
			continue;
		}

		i++;
	}

	return tokens;
}

/**
 * Walk through tokens tracking the PDF text state (position, font size, color)
 * and return info for each text-showing operation (Tj / TJ).
 */
function extractTextOperations(tokens) {
	const results = [];
	let matrixA = 1;
	let posX = 0;
	let posY = 0;
	let fontSize = 12;
	let colorR = 0;
	let colorG = 0;
	let colorB = 0;

	for (let i = 0; i < tokens.length; i++) {
		const t = tokens[i];
		if (t.type !== 'operator') continue;

		switch (t.value) {
			case 'BT':
				matrixA = 1;
				posX = 0;
				posY = 0;
				break;

			case 'Tm':
				// a b c d e f Tm — 6 numeric operands
				if (i >= 6) {
					matrixA = Math.abs(Number.parseFloat(tokens[i - 6].value)) || 1;
					posX = Number.parseFloat(tokens[i - 2].value) || 0;
					posY = Number.parseFloat(tokens[i - 1].value) || 0;
				}

				break;

			case 'Td':
			case 'TD':
				if (i >= 2) {
					posX += Number.parseFloat(tokens[i - 2].value) || 0;
					posY += Number.parseFloat(tokens[i - 1].value) || 0;
				}

				break;

			case 'Tf':
				// /FontName size Tf
				if (i >= 2) {
					fontSize = Number.parseFloat(tokens[i - 1].value) || 12;
				}

				break;

			case 'rg':
				if (i >= 3) {
					colorR = Number.parseFloat(tokens[i - 3].value) || 0;
					colorG = Number.parseFloat(tokens[i - 2].value) || 0;
					colorB = Number.parseFloat(tokens[i - 1].value) || 0;
				}

				break;

			case 'g':
				if (i >= 1) {
					const gray = Number.parseFloat(tokens[i - 1].value) || 0;
					colorR = gray;
					colorG = gray;
					colorB = gray;
				}

				break;

			case 'sc':
			case 'scn':
				// Generic fill color – assume RGB (3 operands) or gray (1 operand)
				if (
					i >= 3 &&
					tokens[i - 1].type === 'number' &&
					tokens[i - 2].type === 'number' &&
					tokens[i - 3].type === 'number'
				) {
					colorR = Number.parseFloat(tokens[i - 3].value) || 0;
					colorG = Number.parseFloat(tokens[i - 2].value) || 0;
					colorB = Number.parseFloat(tokens[i - 1].value) || 0;
				} else if (i >= 1 && tokens[i - 1].type === 'number') {
					const grayVal = Number.parseFloat(tokens[i - 1].value) || 0;
					colorR = grayVal;
					colorG = grayVal;
					colorB = grayVal;
				}

				break;

			case 'k': {
				// CMYK fill color → convert to RGB
				if (i >= 4) {
					const c = Number.parseFloat(tokens[i - 4].value) || 0;
					const m = Number.parseFloat(tokens[i - 3].value) || 0;
					const y = Number.parseFloat(tokens[i - 2].value) || 0;
					const k = Number.parseFloat(tokens[i - 1].value) || 0;
					colorR = (1 - c) * (1 - k);
					colorG = (1 - m) * (1 - k);
					colorB = (1 - y) * (1 - k);
				}

				break;
			}

			case 'Tj':
				if (i >= 1 && tokens[i - 1].type === 'string') {
					results.push({
						text: tokens[i - 1].value,
						x: posX,
						y: posY,
						effectiveFontSize: fontSize * matrixA,
						colorR,
						colorG,
						colorB,
					});
				}

				break;

			case 'TJ':
				if (i >= 1 && tokens[i - 1].type === 'array') {
					// Concatenate all string fragments inside the TJ array
					let fullText = '';
					const innerTokens = tokenizeContentStream(tokens[i - 1].value);
					for (const it of innerTokens) {
						if (it.type === 'string') fullText += it.value;
					}

					if (fullText) {
						results.push({
							text: fullText,
							x: posX,
							y: posY,
							effectiveFontSize: fontSize * matrixA,
							colorR,
							colorG,
							colorB,
						});
					}
				}

				break;

			default:
				break;
		}
	}

	return results;
}

/**
 * Decode the content stream(s) for a page into a single Latin-1 string.
 */
function decodePageContentStream(page, pdfDoc) {
	const contentsObj = page.node.lookup(PDFName.of('Contents'));
	if (!contentsObj) return null;

	const decodeStream = (stream) => {
		if (!stream?.contents) return null;
		const encoded = stream.contents;
		const filter = stream.dict.get(PDFName.of('Filter'));
		const isFlate = filter && filter.toString() === '/FlateDecode';
		try {
			const decoded = isFlate ? pako.inflate(encoded) : encoded;
			return bytesToLatin1(decoded);
		} catch {
			return null;
		}
	};

	if (contentsObj instanceof PDFArray) {
		let fullText = '';
		for (let i = 0; i < contentsObj.size(); i++) {
			const stream = pdfDoc.context.lookup(contentsObj.get(i));
			const text = decodeStream(stream);
			if (text) fullText += text;
		}

		return fullText;
	}

	return decodeStream(contentsObj);
}

function hexToRgb(hex) {
	const clean = hex.replace('#', '');
	return rgb(
		Number.parseInt(clean.slice(0, 2), 16) / 255,
		Number.parseInt(clean.slice(2, 4), 16) / 255,
		Number.parseInt(clean.slice(4, 6), 16) / 255
	);
}

export async function scanPdfFields(pdfBytes) {
	const pdfDoc = await PDFDocument.load(pdfBytes);
	const results = [];

	// Scan AcroForm fields
	const form = pdfDoc.getForm();
	const fields = form.getFields();
	for (const field of fields) {
		try {
			results.push({
				source: 'form',
				name: field.getName(),
				type: field.constructor.name,
				value: field.getText?.() ?? '',
			});
		} catch {
			results.push({
				source: 'form',
				name: field.getName(),
				type: field.constructor.name,
				value: '(unable to read)',
			});
		}
	}

	// Scan annotations (e.g. FreeText added by macOS Preview)
	for (let pageIdx = 0; pageIdx < pdfDoc.getPageCount(); pageIdx++) {
		const page = pdfDoc.getPage(pageIdx);
		const annotsRaw = page.node.get(PDFName.of('Annots'));
		if (!annotsRaw) continue;
		const annots = pdfDoc.context.lookup(annotsRaw);
		if (!(annots instanceof PDFArray)) continue;

		for (let i = 0; i < annots.size(); i++) {
			const annotDict = pdfDoc.context.lookup(annots.get(i));
			if (!annotDict?.get) continue;
			const subtype = annotDict.get(PDFName.of('Subtype'))?.toString();

			// Skip Widget annotations (form fields) — already captured above
			if (subtype === '/Widget') continue;

			const contentsObj = annotDict.get(PDFName.of('Contents'));
			if (!contentsObj) continue;

			let text = '';
			if (contentsObj instanceof PDFHexString) {
				text = contentsObj.decodeText();
			} else if (contentsObj instanceof PDFString) {
				text = contentsObj.asString();
			} else {
				text = contentsObj.toString().replace(/^\(|\)$/g, '');
			}

			if (text.trim()) {
				results.push({
					source: 'annotation',
					name: `Annot-p${pageIdx + 1}-${i}`,
					type: subtype?.replace('/', '') || 'Annotation',
					value: text,
					pageIndex: pageIdx,
					annotIndex: i,
				});
			}
		}
	}

	// Scan content stream text
	for (let pageIdx = 0; pageIdx < pdfDoc.getPageCount(); pageIdx++) {
		const page = pdfDoc.getPage(pageIdx);
		const streamText = decodePageContentStream(page, pdfDoc);
		if (!streamText) continue;
		const tokens = tokenizeContentStream(streamText);
		const textOps = extractTextOperations(tokens);
		for (let opIdx = 0; opIdx < textOps.length; opIdx++) {
			const op = textOps[opIdx];
			if (op.text.trim()) {
				results.push({
					source: 'stream',
					name: `Stream-p${pageIdx + 1}-${opIdx}`,
					type: `${Math.round(op.effectiveFontSize)}pt`,
					value: op.text,
					pageIndex: pageIdx,
					x: op.x,
					y: op.y,
					effectiveFontSize: op.effectiveFontSize,
					colorR: op.colorR,
					colorG: op.colorG,
					colorB: op.colorB,
				});
			}
		}
	}

	return results;
}

export async function generateFromFieldOverrides(
	pdfBytes,
	overrides,
	scannedFields,
	fieldStyles = {}
) {
	const pdfDoc = await PDFDocument.load(pdfBytes);
	pdfDoc.registerFontkit(fontkit);
	const regularFont = await pdfDoc.embedFont(StandardFonts.Helvetica);

	// Cache embedded fonts to avoid re-embedding the same font
	const fontCache = new Map();
	let anonymousFontCounter = 0;
	const embedFont = async (fontBytes, fontName) => {
		if (!fontBytes) return regularFont;
		const cacheKey = fontName || `_anonymous_${++anonymousFontCounter}`;
		if (fontCache.has(cacheKey)) return fontCache.get(cacheKey);
		try {
			const embedded = await pdfDoc.embedFont(fontBytes);
			fontCache.set(cacheKey, embedded);
			return embedded;
		} catch (err) {
			console.error('Failed to embed custom font:', err);
			return regularFont;
		}
	};

	// Helper to resolve styles for a field
	const getStyles = (name) => {
		const styles = fieldStyles[name] || {};
		const hasCustom =
			styles.fontBytes ||
			styles.fontSize ||
			styles.colorHex ||
			styles.offsetX ||
			styles.offsetY;
		return { styles, hasCustom };
	};

	const getColor = (styles, fallbackR, fallbackG, fallbackB) => {
		if (styles.colorHex && /^#?[\dA-Fa-f]{6}$/.test(styles.colorHex)) {
			return hexToRgb(styles.colorHex);
		}

		if (fallbackR !== undefined) {
			return rgb(fallbackR, fallbackG, fallbackB);
		}

		return rgb(0, 0, 0);
	};

	// --- Form fields ---
	const form = pdfDoc.getForm();
	const fields = form.getFields();
	const styledFormFields = []; // Fields cleared via form API, then redrawn with custom styling

	for (const field of fields) {
		const fieldName = field.getName();
		const override = overrides[fieldName];
		if (override === undefined || override === null) continue;

		const { styles, hasCustom } = getStyles(fieldName);

		try {
			if (hasCustom) {
				// Save widget position for manual drawing after flatten
				const widgets = field.acroField.getWidgets();
				if (widgets.length > 0) {
					const rect = widgets[0].getRectangle();
					const pageRef = widgets[0].P();
					styledFormFields.push({
						override,
						styles,
						rect,
						pageRef,
					});
				}

				// Clear the field text so flattening removes the old text
				field.setText('');
				field.updateAppearances(regularFont);
			} else {
				const font = await embedFont(styles.fontBytes, styles.fontName);
				field.setText(override);
				field.updateAppearances(font);
			}
		} catch {
			// skip non-text fields
		}
	}

	// Flatten all fields (clears styled fields' old text, bakes unstyled fields)
	for (const field of form.getFields()) {
		try {
			field.enableReadOnly();
			form.flattenField(field.getName());
		} catch {
			// skip
		}
	}

	// Draw styled form field replacements on top of the now-cleared areas
	for (const entry of styledFormFields) {
		const { override, styles, rect, pageRef } = entry;
		const font = await embedFont(styles.fontBytes, styles.fontName);
		const size = styles.fontSize || rect.height * 0.65;
		const color = getColor(styles);

		const pages = pdfDoc.getPages();
		let page;
		if (pageRef) {
			page = pages.find((p) => p.ref === pageRef);
		}

		if (!page) page = pages[0];

		let drawX;
		if (styles.centerHorizontally) {
			const { width: pageWidth } = page.getSize();
			const textWidth = font.widthOfTextAtSize(override, size);
			drawX = (pageWidth - textWidth) / 2;
		} else {
			drawX = rect.x + 2 + (styles.offsetX || 0);
		}

		page.drawText(override, {
			x: drawX,
			y: rect.y + rect.height * 0.25 + (styles.offsetY || 0),
			size,
			font,
			color,
		});
	}

	// --- Annotation overrides (e.g. FreeText from macOS Preview) ---
	for (let pageIdx = 0; pageIdx < pdfDoc.getPageCount(); pageIdx++) {
		const page = pdfDoc.getPage(pageIdx);
		const annotsRaw = page.node.get(PDFName.of('Annots'));
		if (!annotsRaw) continue;
		const annots = pdfDoc.context.lookup(annotsRaw);
		if (!(annots instanceof PDFArray)) continue;

		const overriddenIndices = new Set();

		for (let i = 0; i < annots.size(); i++) {
			const key = `Annot-p${pageIdx + 1}-${i}`;
			const override = overrides[key];
			if (override === undefined || override === null) continue;

			const annotDict = pdfDoc.context.lookup(annots.get(i));
			if (!annotDict?.get) continue;

			const { styles } = getStyles(key);

			try {
				// Read the annotation's rectangle for positioning
				const rectArray = annotDict.get(PDFName.of('Rect'));
				if (!rectArray || !(rectArray instanceof PDFArray)) continue;
				const coords = [];
				for (let j = 0; j < rectArray.size(); j++) {
					coords.push(
						Number.parseFloat(
							pdfDoc.context.lookup(rectArray.get(j)).toString()
						)
					);
				}

				const rect = {
					x: Math.min(coords[0], coords[2]),
					y: Math.min(coords[1], coords[3]),
					width: Math.abs(coords[2] - coords[0]),
					height: Math.abs(coords[3] - coords[1]),
				};

				const font = await embedFont(styles.fontBytes, styles.fontName);
				const size = styles.fontSize || rect.height * 0.65;
				const color = getColor(styles);

				let annotDrawX;
				if (styles.centerHorizontally) {
					const { width: pageWidth } = page.getSize();
					const textWidth = font.widthOfTextAtSize(override, size);
					annotDrawX = (pageWidth - textWidth) / 2;
				} else {
					annotDrawX = rect.x + 2 + (styles.offsetX || 0);
				}

				page.drawText(override, {
					x: annotDrawX,
					y: rect.y + rect.height * 0.25 + (styles.offsetY || 0),
					size,
					font,
					color,
				});

				overriddenIndices.add(i);
			} catch {
				// skip
			}
		}

		// Remove overridden annotations from the page entirely
		if (overriddenIndices.size > 0) {
			const newAnnots = pdfDoc.context.obj([]);
			for (let i = 0; i < annots.size(); i++) {
				if (!overriddenIndices.has(i)) {
					newAnnots.push(annots.get(i));
				}
			}

			page.node.set(PDFName.of('Annots'), newAnnots);
		}
	}

	// --- Stream text overrides (white-out old text and redraw) ---
	const streamOverrides = (scannedFields || []).filter(
		(f) =>
			f.source === 'stream' &&
			overrides[f.name] !== undefined &&
			overrides[f.name] !== null
	);
	for (const field of streamOverrides) {
		const page = pdfDoc.getPage(field.pageIndex);
		const { width } = page.getSize();
		const { styles } = getStyles(field.name);
		const font = await embedFont(styles.fontBytes, styles.fontName);
		const size = styles.fontSize || field.effectiveFontSize;
		const color = getColor(styles, field.colorR, field.colorG, field.colorB);

		// White-out the original text using the original dimensions
		const origSize = field.effectiveFontSize;
		const textWidth = Math.min(
			regularFont.widthOfTextAtSize(field.value, origSize) + 10,
			width * 0.65
		);
		page.drawRectangle({
			x: field.x - 2,
			y: field.y - origSize * 0.25,
			width: textWidth,
			height: origSize * 1.2,
			color: rgb(1, 1, 1),
			borderWidth: 0,
		});

		// Draw replacement text with custom styling
		let streamDrawX;
		if (styles.centerHorizontally) {
			const textWidth2 = font.widthOfTextAtSize(overrides[field.name], size);
			streamDrawX = (width - textWidth2) / 2;
		} else {
			streamDrawX = field.x + (styles.offsetX || 0);
		}

		page.drawText(overrides[field.name], {
			x: streamDrawX,
			y: field.y + (styles.offsetY || 0),
			size,
			font,
			color,
		});
	}

	return pdfDoc.save();
}
